import { useContext, useMemo } from 'react';
import useSWR from 'swr';
import { MainContext } from '../App';
import useGerenciasUser from './useGerenciasUser';
import { getUsuarios } from '../services/request/requestUsuarios';
import { getContratos, getGerencias } from '../services/request/requestPortafolio';
import { VER_SELECCION_GERENCIAS, VER_CONTRATOS_GERENCIA } from '../constants/permissions';

const OPCIONES_SWR = { revalidateOnFocus: false };

//Une listados eliminando duplicados por _id.
const unirSinDuplicados = (listas) => {
  const porId = new Map();
  listas.flat().forEach((item) => item && porId.set(item._id, item));
  return Array.from(porId.values());
};

/**
 * Hook que obtiene las personas, contratos y gerencias a las que el usuario en sesión puede asignar activos.
 * - verSeleccionGerencias: todas.
 * - verContratosGerencia: las de su gerencia y las de las gerencias adicionales que tenga habilitadas (verGerenciaXXX).
 * - Resto: las de su contrato y las de las gerencias adicionales que tenga habilitadas.
 */
export default function useOpcionesAsignacion() {
  const { usuarioSesion, permisos } = useContext(MainContext);
  const { gerencias: gerenciasAdicionales, cargado } = useGerenciasUser(false);

  const verTodas = Boolean(permisos[VER_SELECCION_GERENCIAS]);
  const verGerencia = Boolean(permisos[VER_CONTRATOS_GERENCIA]);
  const gerenciaUsuario = usuarioSesion && usuarioSesion.gerencia;
  const contratoUsuario = usuarioSesion && usuarioSesion.contrato;

  //Gerencias cuyos contratos y personas completos puede ver el usuario (sin repetir la propia).
  const idsGerencias = useMemo(() => {
    const ids = gerenciasAdicionales.map((gerencia) => gerencia._id);
    if (verGerencia && gerenciaUsuario) ids.push(gerenciaUsuario);
    return Array.from(new Set(ids)).sort();
  }, [gerenciasAdicionales, verGerencia, gerenciaUsuario]);

  //La clave incluye el alcance para no compartir caché con otras pantallas ni entre usuarios con permisos distintos.
  const alcance = verTodas
    ? 'todas'
    : cargado
    ? `${verGerencia ? 'gerencia' : `contrato:${contratoUsuario}`}:${idsGerencias.join(',')}`
    : null;

  const { data: usuarios, isValidating: isValidatingUsuarios } = useSWR(
    alcance && `asignacion:personas:${alcance}`,
    async () => {
      if (verTodas) return getUsuarios({ is_gsuite_talana: true });
      const consultas = idsGerencias.map((id) => getUsuarios({ gerencia_ref: id, is_gsuite_talana: true }));
      if (!verGerencia) consultas.push(getUsuarios({ 'contratos_ref.contrato_id': contratoUsuario, is_gsuite_talana: true }));
      return unirSinDuplicados(await Promise.all(consultas));
    },
    OPCIONES_SWR,
  );

  const { data: contratos, isValidating: isValidatingContratos } = useSWR(
    alcance && `asignacion:contratos:${alcance}`,
    async () => {
      if (verTodas) return getContratos();
      const consultas = idsGerencias.map((id) => getContratos({ gerencia_ref: id }));
      if (!verGerencia) consultas.push(getContratos({ _id: contratoUsuario }));
      const respuestas = await Promise.all(consultas);
      //Se mantiene la forma de la respuesta de getContratos ({ data: [...] }).
      return { data: unirSinDuplicados(respuestas.map((respuesta) => respuesta.data || [])) };
    },
    OPCIONES_SWR,
  );

  const { data: gerencias, isValidating: isValidatingGerencias } = useSWR(
    alcance && `asignacion:gerencias:${alcance}`,
    async () => {
      if (verTodas) return getGerencias();
      const propia = gerenciaUsuario ? await getGerencias({ _id: gerenciaUsuario }) : [];
      return unirSinDuplicados([propia || [], gerenciasAdicionales]);
    },
    OPCIONES_SWR,
  );

  return {
    usuarios,
    contratos,
    gerencias,
    isValidatingUsuarios: isValidatingUsuarios || !alcance,
    isValidatingContratos: isValidatingContratos || !alcance,
    isValidatingGerencias: isValidatingGerencias || !alcance,
  };
}
